import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePortalNavigate, usePortalPath } from '@/hooks/usePortalPath';
import {
  actionAuthSubmitButtonClassName,
  actionFormInputClassName,
  actionLinkClassName,
} from '@core/theme/actionTheme.util';
import {
  ERROR_CODE_INCORRECT_APP_CONTEXT,
  findErrorByCode,
} from '@core/util/graphqlError.util';
import { useAuth } from '@/contexts/AuthContext';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { Spinner } from '@/components/shared/Spinner';

const Login = () => {
  const location = useLocation();
  const portalNavigate = usePortalNavigate();
  const { path: portalPath } = usePortalPath();
  const { signIn, checkAuth, isLoading: isAuthLoading } = useAuth();
  const [formState, setFormState] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const redirectPath = location.state?.from?.pathname;
      const result = await signIn({
        email: formState.email,
        password: formState.password,
        redirectPath,
      });

      if (result.success) {
        // Successfully signed in, checkAuth already called
      } else if (result.requiresConfirmation && result.email) {
        portalNavigate('/confirm-signup', { state: { email: result.email } });
      } else if (result.requiresNewPassword && result.email) {
        portalNavigate('/set-new-password', { state: { email: result.email } });
      } else if (result.requiresPasswordReset && result.email) {
        portalNavigate('/forgot-password', {
          state: { email: result.email, passwordResetRequired: true },
        });
      }
    } catch (error: any) {
      let errorMessage =
        error instanceof Error ? error.message : 'An error occurred';

      // If the error is an app context error, display message to user and return.
      const incorrectAppContextError = findErrorByCode(
        error,
        ERROR_CODE_INCORRECT_APP_CONTEXT,
      );
      errorMessage = incorrectAppContextError?.message || errorMessage;
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const redirectPath = location.state?.from?.pathname;
    void checkAuth(redirectPath).catch(() => {
      // No session is expected when the user is not signed in.
    });
  }, [checkAuth, location.state]);

  return isAuthLoading ? (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <Spinner />
    </div>
  ) : (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <div className="w-full max-w-md px-6 py-8">
        <AuthHeader title="Sign in to your account" />

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className={actionFormInputClassName}
                placeholder="Enter your email"
                value={formState.email}
                onChange={(e) =>
                  setFormState({ ...formState, email: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className={actionFormInputClassName}
                placeholder="Enter your password"
                value={formState.password}
                onChange={(e) =>
                  setFormState({ ...formState, password: e.target.value })
                }
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Link
              to={portalPath('/forgot-password')}
              className={`cursor-pointer text-sm font-medium ${actionLinkClassName}`}
            >
              Forgot your password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`${actionAuthSubmitButtonClassName} disabled:opacity-50`}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account yet?{' '}
            <Link
              to={portalPath('/signup')}
              className={`cursor-pointer font-medium ${actionLinkClassName}`}
              aria-label="Sign up"
              tabIndex={0}
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
